import {BehaviorSubject, distinctUntilChanged, interval, Observable, throttle} from "rxjs";
import {Comparator} from "../comparators/comparator.interface";
import {DefaultComparator} from "../comparators/default.comparator";
import {ArrayHelper} from "../helpers/array.helper";
import {Validator} from "./validator.interface";
import {ApplyMoment} from "./validators/apply-moment.enum";
import {RequiredValidator} from "./validators/required.validator";
import {FormItem} from "./form-item.interface";

export class FieldProxy<TValue> implements FormItem<TValue> {

    private readonly _changedValue$: BehaviorSubject<TValue>;

    private _currentValue: TValue;
    private readonly _isRequired: boolean;
    private _lastValue: TValue;
    private readonly _validators: Validator[];

    public constructor(
        private readonly _key: string,
        private _initialValue: TValue,
        private readonly _comparator: Comparator<any> = new DefaultComparator,
        ...validators: Validator[]
    ) {
        this._lastValue = this._initialValue;
        this._currentValue = this._initialValue;

        this._changedValue$ = new BehaviorSubject<TValue>(this._currentValue);
        this._validators = validators;

        this._errors = [];

        if (this._validators.length > 0) {
            this._isRequired = validators.some(v => v instanceof RequiredValidator);
            this._changedValue$.subscribe(() => this._validate(ApplyMoment.ON_CHANGE));
        } else {
            this._isRequired = false;
        }

        this._validate(ApplyMoment.ON_INIT);
    }

    private _errors: string[] = [];

    public get errors(): { [key: string]: string[] } {
        return {[this._key]: this._errors};
    }

    public get initialValue(): TValue {
        return this._initialValue;
    }

    /**
     * The field's value whenever it changes, throttled to at most one report per 100 ms. Trailing is
     * on: the last value of a quick burst is reported when the window ends, so the form's change
     * stream and a dialog's save state never stay on an earlier value.
     */
    public get changedValue$(): Observable<TValue> {
        return this._changedValue$.pipe(
            distinctUntilChanged((previous, current) => this._comparator.isEqualTo(previous, current)),
            throttle(() => interval(100), {leading: true, trailing: true})
        );
    }

    public get dirty(): boolean {
        return !this._comparator.isEqualTo(this._currentValue, this._initialValue);
    }

    public get hasChanged(): boolean {
        return !this._comparator.isEqualTo(this._lastValue, this._currentValue);
    }

    public get isRequired(): boolean {
        return this._isRequired;
    }

    public get valid(): boolean {
        return this._errors.length === 0;
    }

    public get value(): TValue {
        return this._currentValue;
    }

    public set value(newValue: TValue) {
        if (!this._comparator.isEqualTo(newValue, this._currentValue)) {
            this._lastValue = this._currentValue;
            this._currentValue = newValue;
            this._changedValue$.next(newValue);
        }
    }

    public get key(): string {
        return this._key;
    }

    public reset(): void {
        this._lastValue = this.value = this._initialValue;
    }

    public commit() {
        this._initialValue = this._currentValue;
        this._lastValue = this._currentValue;
    }

    public clone(): FormItem<TValue> {
        return new FieldProxy(
            this._key,
            this._currentValue,
            this._comparator,
            ...this._validators
        );
    }

    private _validate(applyMoment: ApplyMoment): void {
        this._errors = this._validators
            .filter(validator => validator.applyMoment.includes(applyMoment))
            .map(validator => validator.isValid(this.value))
            .filter(v => ArrayHelper.notEmpty(v)) as string[];
    }
}
